import { WhatsappRecord } from "@bandido/shared";
import { theme } from "@frontend/theme";
import { Close } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { useWhatsappClient } from "../WhatsappClientContext";

/**
 * Formats a WhatsApp message timestamp in a user-friendly way
 * - Less than 1 minute: "Ahora"
 * - Less than 1 hour: "Hace 15 min"
 * - Today: "14:30"
 * - Yesterday: "Ayer 14:30"
 * - This week: "Lunes 14:30"
 * - Older: "15 Ago 14:30"
 * - Different year: "15 Ago 2023 14:30"
 */

export const useformatWhatsAppTimestamp = () => {
  const { t } = useTranslation();

  return (timestamp: string | Date): string => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));
    
    // Less than 1 minute
    if (diffInMinutes < 1) {
      return t('whatsapp.content.now');
    }
    
    // Less than 1 hour
    if (diffInMinutes < 60) {
      return t('whatsapp.content.minutesAgo', { minutes: diffInMinutes });
    }
    
    const timeFormat = messageDate.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const isToday = messageDate.toDateString() === now.toDateString();
    if (isToday) {
      return timeFormat;
    }
    
    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return `Ayer ${timeFormat}`;
    }
    
    // This week (last 7 days)
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    if (messageDate > weekAgo) {
      const dayName = messageDate.toLocaleDateString('es-ES', { weekday: 'long' });
      const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `${capitalizedDay} ${timeFormat}`;
    }
    
    // Same year
    if (messageDate.getFullYear() === now.getFullYear()) {
      const dayMonth = messageDate.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
      return `${dayMonth} ${timeFormat}`;
    }
    
    // Different year
    const fullDate = messageDate.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    return `${fullDate} ${timeFormat}`;
  }
};

export const ReplyMessageIndicator = ({
  messageToReply,
  clientId,
}: {
  messageToReply: WhatsappRecord,
  clientId: string,
}) => {
  const formatMessageTimestamp = useformatWhatsAppTimestamp();
  const { setMessageToReply } = useWhatsappClient();

  return (
    <Box
      py={0.5}
      px={1}
      borderRadius={1}
      width="100%"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor={messageToReply.clientId === clientId ? "grey.100" : "rgba(255, 255, 255, 0.1)"}
      borderLeft={`2px solid ${theme.palette.primary.main}`}
      sx={{ opacity: 0.8 }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {messageToReply.senderName === "BANDIDO_SYSTEM" ? t('whatsapp.systemSender') : messageToReply.senderName}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize={10}>
            {`${formatMessageTimestamp(messageToReply.createdAt)}`}
          </Typography>
        </Box>
        <br />
        <Typography variant="caption" color="text.secondary">
          {messageToReply.type === 'text' ? 
          messageToReply.text && messageToReply.text.length > 15 ? messageToReply.text.slice(0, 15) + '...' : messageToReply.text : 
            `${messageToReply.type.charAt(0).toUpperCase() + messageToReply.type.slice(1)}`
          }
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => setMessageToReply(null)}>
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};
